/**
 * usePersons
 *
 * React Query hooks for Person entity.
 */

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { searchPersons, getPersonById, getMyPerson, updatePerson, createPerson, checkinMatch, linkCheckin } from '../services/personService';
import type { Person, PersonFormData, CheckinMatchInput } from '../types';

/** Enough signal to run an identity match: a scanned band, a document, an
 * email, or a full name. A bare first name alone is not queried. */
export function hasCheckinMatchSignal(input: CheckinMatchInput): boolean {
  return Boolean(
    input.nfcUid?.trim()
    || input.documentNumber?.trim()
    || input.email?.trim()
    || (input.firstName?.trim() && input.lastName?.trim()),
  );
}

interface PersonFilters {
  search?: string;
  role?: Person['currentRole'];
  documentNumber?: string;
  hasUser?: boolean;
}

export function usePersons(filters: PersonFilters = {}) {
  return useQuery({
    queryKey: ['persons', filters],
    queryFn: () => searchPersons(filters),
    staleTime: 30_000,
  });
}

export function usePerson(id: string | null) {
  return useQuery({
    queryKey: ['person', id],
    queryFn: () => getPersonById(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useMyPerson(enabled = true) {
  return useQuery({
    queryKey: ['person', 'me'],
    queryFn: () => getMyPerson(),
    staleTime: 60_000,
    enabled,
  });
}

export function useUpdatePerson() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PersonFormData }) =>
      updatePerson(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['person', data.id] });
      queryClient.invalidateQueries({ queryKey: ['persons'] });
      queryClient.invalidateQueries({ queryKey: ['person', 'me'] });
    },
  });
}

export function useCreatePerson() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PersonFormData) => createPerson(input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['persons'] });
      queryClient.invalidateQueries({ queryKey: ['person', data.id] });
    },
  });
}


// ── Identity-based dedup hook ─────────────────────────────────────────
//
// usePersonMatchByIdentity — debounced live lookup used by the walk-in
// modal and the manual booking guest step. Fires two parallel
// queries (`?search=` for email, `?document_number=` for the ID) and
// surfaces a single returning-guest hit, a conflict (different rows for
// each key), or nothing at all. Backend dedup runs at submit time too;
// this hook is purely for UI feedback as the agent types.
//
// We use `?search=` for email lookup because the existing endpoint
// already matches against email/name/document. Precision is good
// enough for live UI; a dedicated `?email=` filter is a future
// optimization if the index gets too noisy.

export type PersonMatchKind = 'none' | 'unique' | 'conflict';

export interface PersonMatchResult {
  kind: PersonMatchKind;
  /** Strongest single match — populated for 'unique', and on 'conflict' when the operator hasn't chosen yet. */
  strongerMatch: Person | null;
  matchByEmail: Person | null;
  matchByDocument: Person | null;
  isLoading: boolean;
}

/** Debounce a value — reuses a small inline pattern instead of pulling lodash. */
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

export function usePersonMatchByIdentity(input: {
  email?: string;
  documentNumber?: string;
  /** Skip the lookup entirely (e.g. dialog closed). */
  enabled?: boolean;
  debounceMs?: number;
}): PersonMatchResult {
  const debounce = input.debounceMs ?? 300;
  const enabled = input.enabled ?? true;
  const debouncedEmail = useDebouncedValue((input.email ?? '').trim().toLowerCase(), debounce);
  const debouncedDoc = useDebouncedValue((input.documentNumber ?? '').trim(), debounce);

  const emailQuery = useQuery({
    queryKey: ['person-match', 'email', debouncedEmail],
    queryFn: () => searchPersons({ search: debouncedEmail }),
    enabled: enabled && debouncedEmail.length >= 3 && debouncedEmail.includes('@'),
    staleTime: 30_000,
  });
  const docQuery = useQuery({
    queryKey: ['person-match', 'document', debouncedDoc],
    queryFn: () => searchPersons({ documentNumber: debouncedDoc }),
    enabled: enabled && debouncedDoc.length >= 3,
    staleTime: 30_000,
  });

  // Pick the most precise candidate from each result set: an exact
  // match on the field beats a fuzzy `?search=` hit.
  const matchByEmail = (emailQuery.data ?? []).find(
    (p) => (p.email ?? '').toLowerCase() === debouncedEmail,
  ) ?? null;
  const matchByDocument = (docQuery.data ?? []).find(
    (p) => (p.documentNumber ?? '').toLowerCase() === debouncedDoc.toLowerCase(),
  ) ?? null;

  let kind: PersonMatchKind = 'none';
  let strongerMatch: Person | null = null;
  if (matchByEmail && matchByDocument) {
    if (matchByEmail.id === matchByDocument.id) {
      kind = 'unique';
      strongerMatch = matchByDocument;
    } else {
      kind = 'conflict';
      strongerMatch = matchByDocument; // Document is the stronger key.
    }
  } else if (matchByDocument) {
    kind = 'unique';
    strongerMatch = matchByDocument;
  } else if (matchByEmail) {
    kind = 'unique';
    strongerMatch = matchByEmail;
  }

  return {
    kind,
    strongerMatch,
    matchByEmail,
    matchByDocument,
    isLoading: emailQuery.isFetching || docQuery.isFetching,
  };
}

/**
 * useCheckinMatch — front-desk identity recovery at check-in.
 *
 * Pass the scanned wristband and/or the details staff have typed; returns
 * ranked candidates (strong → possible) with a stay history. Only fires when
 * there is enough signal (see hasCheckinMatchSignal); debounce the input in
 * the caller so it doesn't query on every keystroke.
 */
export function useCheckinMatch(input: CheckinMatchInput, enabled = true) {
  return useQuery({
    queryKey: ['checkin-match', input],
    queryFn: () => checkinMatch(input),
    enabled: enabled && hasCheckinMatchSignal(input),
    staleTime: 15_000,
  });
}

/**
 * useLinkCheckin — consolidate a fresh booking person (source) into an existing
 * returning person (target). Destructive; the caller must confirm first.
 */
export function useLinkCheckin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ targetId, sourcePersonId }: { targetId: string; sourcePersonId: string }) =>
      linkCheckin(targetId, sourcePersonId),
    onSuccess: (person) => {
      queryClient.invalidateQueries({ queryKey: ['persons'] });
      queryClient.invalidateQueries({ queryKey: ['person', person.id] });
      queryClient.invalidateQueries({ queryKey: ['guests'] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      // The booking detail + its guest list are the source of the check-in
      // modal's guests. Without refreshing them, the consolidated guest keeps
      // its stale (source) person id, so re-opening the form re-offers the same
      // match. Refetch so the guest now resolves to the kept (target) person.
      queryClient.invalidateQueries({ queryKey: ['bookingDetail'] });
      queryClient.invalidateQueries({ queryKey: ['bookingGuests'] });
    },
  });
}
